import { Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React from "react";

import { activityApi } from "apis";
import { getMetricsByCategory } from "constants/metrics";
import {
  MetricValueAggregatedResponseDto,
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "libs/cellera-sdk";
import MetricCard, { MetricData } from "./MetricCard";
import SectionWrapper from "./SectionWrapper";

// Get vital metrics from centralized configuration
const VITAL_METRICS_MAP = getMetricsByCategory("vital");

type VitalMetricCode = keyof typeof VITAL_METRICS_MAP;

interface VitalSignsProps {
  patientId: string;
  onViewHistory: () => void;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "No Data";

  // Handle blood pressure (JSON format: {systolic: 120, diastolic: 80})
  if (typeof value === "object" && value !== null) {
    const bp = value as { systolic: number; diastolic: number };
    if (typeof bp.systolic === "number" && typeof bp.diastolic === "number") {
      return `${bp.systolic}/${bp.diastolic}`;
    }
    return "No Data";
  }

  // Handle numeric values
  if (typeof value === "number") {
    return value.toString();
  }

  return "No Data";
};

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No recent data available",
  suggestion: "Consider manual measurement",
  lastRecorded: "Not available",
});

const VitalSigns: React.FC<VitalSignsProps> = ({
  patientId,
  onViewHistory,
}) => {
  // Individual metric calls following mobile app pattern
  const vitalSignsQueries = [
    {
      queryKey: "heart_rate",
      metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRate,
    },
    {
      queryKey: "blood_pressure",
      metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.BloodPressure,
    },
    {
      queryKey: "blood_oxygen_saturation",
      metricCode:
        MetricValuesAggregationRequestDtoMetricCodeEnum.BloodOxygenSaturation,
    },
    {
      queryKey: "heart_rate_variability",
      metricCode:
        MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRateVariability,
    },
    {
      queryKey: "blood_glucose",
      metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.BloodGlucose,
    },
    {
      queryKey: "respiratory_rate",
      metricCode:
        MetricValuesAggregationRequestDtoMetricCodeEnum.RespiratoryRate,
    },
    {
      queryKey: "body_temperature",
      metricCode:
        MetricValuesAggregationRequestDtoMetricCodeEnum.BodyTemperature,
    },
  ];

  const [
    { data: heartRateData, isLoading: heartRateLoading },
    { data: bloodPressureData, isLoading: bloodPressureLoading },
    { data: bloodOxygenData, isLoading: bloodOxygenLoading },
    { data: heartRateVariabilityData, isLoading: heartRateVariabilityLoading },
    { data: bloodGlucoseData, isLoading: bloodGlucoseLoading },
    { data: respiratoryRateData, isLoading: respiratoryRateLoading },
    { data: bodyTemperatureData, isLoading: bodyTemperatureLoading },
  ] = useQueries({
    queries: vitalSignsQueries.map((query) => ({
      queryKey: ["vitalSigns", patientId, query.queryKey],
      queryFn: () => {
        if (!patientId) return null;

        const request: MetricValuesAggregationRequestDto = {
          metricCode: query.metricCode,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [
            MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
          ],
          orderBy: "DESC",
        };

        return activityApi.getMetricValuesAggregatedByPatientId({
          patientId,
          metricValuesAggregationRequestDto: request,
        });
      },
      enabled: !!patientId,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  // Map individual data to MetricData format
  const metricDataMap: Record<
    string,
    MetricValueAggregatedResponseDto | undefined
  > = {
    [MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRate]:
      heartRateData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodPressure]:
      bloodPressureData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodOxygenSaturation]:
      bloodOxygenData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRateVariability]:
      heartRateVariabilityData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.BloodGlucose]:
      bloodGlucoseData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.RespiratoryRate]:
      respiratoryRateData?.data[0],
    [MetricValuesAggregationRequestDtoMetricCodeEnum.BodyTemperature]:
      bodyTemperatureData?.data[0],
  };

  const displayData: MetricData[] = Object.keys(VITAL_METRICS_MAP).map(
    (metricCode) => {
      const metricInfo = VITAL_METRICS_MAP[metricCode as VitalMetricCode];
      const latestValue = metricDataMap[metricCode];

      if (!latestValue) {
        return {
          id: metricCode,
          name: metricInfo.name,
          value: "No Data",
          unit: metricInfo.unit,
          lastChecked: "Never",
          hasData: false,
          fallbackInfo: getFallbackInfo(),
        };
      }

      const formattedValue = formatValue(
        latestValue.valueJsonData?.latest ??
          latestValue.valueIntData?.latest ??
          latestValue.valueFloatData?.latest,
      );

      const isNoData = formattedValue === "No Data";

      return {
        id: metricCode,
        name: metricInfo.name,
        value: formattedValue,
        unit: metricInfo.unit,
        lastChecked: new Date(latestValue.startDate).toLocaleString("vi-VN"),
        hasData: !isNoData,
        fallbackInfo: isNoData ? getFallbackInfo() : undefined,
      };
    },
  );

  const isLoading =
    heartRateLoading ||
    bloodPressureLoading ||
    bloodOxygenLoading ||
    heartRateVariabilityLoading ||
    bloodGlucoseLoading ||
    respiratoryRateLoading ||
    bodyTemperatureLoading;

  if (isLoading) {
    return (
      <SectionWrapper title="Health Metrics" onViewAll={onViewHistory}>
        <Typography>Loading health metrics...</Typography>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Health Metrics"
      onViewAll={onViewHistory}
      spacing="compact"
    >
      {displayData.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </SectionWrapper>
  );
};

export default VitalSigns;
